class BlocksForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.priceElement = document.querySelector(`#price-${this.dataset.sectionId}`);
    this.thicknessSelect = this.querySelector('[data-blocks-thickness-select]');
    this.lengthSelect = this.querySelector('[data-blocks-length]');
    this.widthSelect = this.querySelector('[data-blocks-width]');
    this.variantSelect = this.querySelector('[data-blocks-variant-select]');
    this.addToCartButton = this.querySelector('[data-add-to-cart-button]');
    this.shippingInfo = this.querySelector('[data-shipping-info]');

    this.currentVariant = null;
    this.currentBasePrice = 0;

    this.debouncedUpdate = this.debounce(this.updateCalculation.bind(this), 300);

    // Properties for AJAX cart
    this.cartNotification = document.querySelector('cart-notification');
    this.body = document.body;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

    this.bindEvents();
    this.updateAddToCartState();
  }

  parseFraction(value) {
    if (!value) return 0;
    const str = value.toString().trim().replace('"', '');
    if (str.includes('/')) {
      const parts = str.split('/');
      return parseFloat(parts[0]) / parseFloat(parts[1]);
    }
    return parseFloat(str) || 0;
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  }

  bindEvents() {
    this.thicknessSelect.addEventListener('change', this.onThicknessChange.bind(this));
    this.lengthSelect.addEventListener('change', this.debouncedUpdate);
    this.widthSelect.addEventListener('change', this.debouncedUpdate);
  }

  onThicknessChange() {
    const selectedThickness = this.thicknessSelect.value;

    this.currentVariant = null;
    this.currentBasePrice = 0;

    const variantOptions = this.variantSelect.querySelectorAll('option');
    variantOptions.forEach(option => {
      if (option.dataset.thickness === selectedThickness) {
        option.selected = true;
        this.currentVariant = option;
        this.currentBasePrice = parseFloat(option.dataset.basePrice) || 0;
      }
    });

    this.updateCalculation();
    this.updateAddToCartState();
  }

  updateCalculation() {
    if (!this.currentVariant || !this.thicknessSelect.value) {
      this.clearDisplays();
      return;
    }

    const length = parseFloat(this.lengthSelect.value) || 0;
    const width = parseFloat(this.widthSelect.value) || 0;

    if (length > 0 && width > 0) {
      const effectiveLength = Math.max(length, 6);
      const effectiveWidth = Math.max(width, 6);

      const totalCost = effectiveLength * effectiveWidth * this.currentBasePrice;

      this.updateMainPrice(totalCost);
    } else {
      this.clearDisplays();
    }

    this.updateAddToCartState();
  }

  updateMainPrice(price) {
    if (!this.priceElement) return;

    const priceItem = this.priceElement.querySelector('.amount');
    const simplePriceHTML = `<span class="price-item price-item--regular">$${price.toFixed(2)}</span>`;

    if (priceItem) {
      priceItem.innerHTML = `$${price.toFixed(2)}`;
    } else {
      this.priceElement.innerHTML = simplePriceHTML;
    }
  }

  updateAddToCartState() {
    const thicknessSelected = this.thicknessSelect.value !== '';
    const lengthSelected = this.lengthSelect.value !== '';
    const widthSelected = this.widthSelect.value !== '';

    const canAddToCart = thicknessSelected && lengthSelected && widthSelected && this.currentVariant;

    // Check if any dimension exceeds 36 inches
    const length = parseFloat(this.lengthSelect.value) || 0;
    const width = parseFloat(this.widthSelect.value) || 0;
    const exceedsShippingLimit = length > 36 || width > 36;
    this.shippingInfo.style.display = (canAddToCart && exceedsShippingLimit) ? 'block' : 'none';

    this.addToCartButton.disabled = !canAddToCart;
    this.addToCartButton.hidden = !canAddToCart;
    this.addToCartButton.style.display = canAddToCart ? 'inline-flex' : 'none';
  }

  onSubmitHandler(evt) {
    evt.preventDefault();

    if (this.addToCartButton.classList.contains('loading')) return;

    this.addToCartButton.setAttribute('aria-disabled', true);
    this.addToCartButton.classList.add('loading');
    this.handleErrorMessage();

    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      }
    };

    let formData = new FormData(this.form);

    // Calculate and add weight property
    const length = parseFloat(this.lengthSelect.value) || 0;
    const width = parseFloat(this.widthSelect.value) || 0;
    const thickness = this.parseFraction(this.thicknessSelect.value);
    const weight = length * width * thickness * 0.043;
    formData.append('properties[Weight]', weight.toFixed(3));

    formData.append('sections', this.getSectionsToRender().map((section) => section.section));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.addToCartButton.classList.remove('loading');
        this.addToCartButton.removeAttribute('aria-disabled');
      });
  }

  getSectionsToRender() {
    return [{
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer'
    },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) {
        return;
      }
      const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    }));

    this.body.classList.add('open-cc');
    document.getElementById('Cart-Drawer').classList.add('active');
    dispatchCustomEvent('cart-drawer:open');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }
}

customElements.define('blocks-form', BlocksForm);